import { IDataLayerItem, DataLayerItemType, DataSourceType, DataAccessStatus } from '../api/dataLayer.model';
import { Platform } from '../api/common';
import { DetectionRuleSeverity } from '../api/finding.model';
import { ITableRowViewModel } from './tableRowViewModel';
import { getPlatformLogoPath } from '../../common/platformUtils';
import { getDataLayerItemIcon, getDataSourceTypeIcon } from '../../common/dataLayer/dataLayerUtils';
import { StatusLabelEnum } from '../../components/status-label/status-label.component';
import { formatFromDate } from '../../common/datetime_utility';
import { formatFileSize } from '../../common/stringUtils';

export interface IDataLayerTableRowViewModel extends ITableRowViewModel {
  id: string;
  resourceName: string;
  dataType: string;
  sourceType: string;
  platform: string;
  lastAccessed: Date;
  accessCount: number;
  status: string;
  associatedRisk: string;

  // New column properties
  dataItemWithIcon: string;
  dataItemWithIconIcon: string;
  findings: string;
  findingsBySeverity: Record<DetectionRuleSeverity, number>;
  sensitivityLabel: string;

  resourceTypeIcon: string;
  sourceTypeIcon: string;
  platformIcon: string;
  resourceTooltip?: string;

  originalData: IDataLayerItem;
}

export class DataLayerTableRowViewModelHelper {
  public static createViewModel(dataLayerItem: IDataLayerItem): IDataLayerTableRowViewModel {
    const name = dataLayerItem.resourceName;
    const itemType = dataLayerItem.dataType || DataLayerItemType.unknown;
    const itemSource = dataLayerItem.sourceType || DataSourceType.unknown;
    const count = dataLayerItem.counter ?? 0;
    const fileExt = dataLayerItem.file_extension;

    return {
      // IRowViewModel properties
      rowId: dataLayerItem.id,
      clickable: true,
      menuActions: [], // Empty menu actions for Phase 2

      // ITableRowViewModel properties
      statusLabel: this._getStatusLabel(dataLayerItem.status),

      // IDataLayerTableRowViewModel properties
      id: dataLayerItem.id,
      dataType: this._getDisplayDataType(itemType),
      sourceType: this._getDisplaySourceType(itemSource),
      accessCount: count,
      resourceName: dataLayerItem.resourceName,
      platform: this._getDisplayPlatform(dataLayerItem.platform),
      lastAccessed: dataLayerItem.lastAccessed,
      status: this._getDisplayStatus(dataLayerItem.status),
      associatedRisk: this._getAssociatedRisk(dataLayerItem),

      // New column properties
      dataItemWithIconIcon: this._getResourceTypeIcon(dataLayerItem.id, name, itemType, fileExt),
      dataItemWithIcon: dataLayerItem.resourceName,
      findings: this._getFindings(dataLayerItem),
      findingsBySeverity: this._getFindingsBySeverity(dataLayerItem),
      sensitivityLabel: dataLayerItem.sensitivityLabel || '',

      // Icon properties
      resourceTypeIcon: this._getResourceTypeIcon(dataLayerItem.id, name, itemType, fileExt),
      sourceTypeIcon: this._getSourceTypeIcon(itemSource),
      resourceTooltip: this._getResourceTooltip(dataLayerItem),
      platformIcon: this._getPlatformIcon(dataLayerItem.platform),
      originalData: dataLayerItem,
    };
  }

  private static _getDisplayDataType(dataType: DataLayerItemType): string {
    switch (dataType) {
      case DataLayerItemType.file:
        return 'File';
      case DataLayerItemType.document:
        return 'Document';
      case DataLayerItemType.email:
        return 'Email';
      case DataLayerItemType.website:
        return 'Website';
      default:
        return 'Unknown';
    }
  }

  private static _getDisplaySourceType(sourceType: DataSourceType): string {
    switch (sourceType) {
      case DataSourceType.cloudShare:
        return 'Cloud Share';
      case DataSourceType.website:
        return 'Website';
      case DataSourceType.unknown:
        return 'Unknown';
      case DataSourceType.fileUpload:
        return 'File Upload';
      case DataSourceType.cloudFile:
        return 'Cloud File';
      case DataSourceType.database:
        return 'Database';
      case DataSourceType.sharepoint:
      case DataSourceType.sharePoint:
        return 'SharePoint';
      case DataSourceType.fileSystem:
        return 'File System';
      case DataSourceType.oneDrive:
        return 'OneDrive';
      case DataSourceType.teams:
        return 'Teams';
      case DataSourceType.email:
        return 'Email';
      case DataSourceType.api:
        return 'API';
      case DataSourceType.vector:
        return 'Vector Store';
      case DataSourceType.embedding:
        return 'Embedding';
      default:
        return 'Other';
    }
  }

  private static _getDisplayPlatform(platform: Platform): string {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  private static _getFormattedLastAccessed(lastAccessed: Date): string {
    return formatFromDate(lastAccessed);
  }

  private static _getDisplayStatus(status: DataAccessStatus): string {
    switch (status) {
      case DataAccessStatus.active:
        return 'Active';
      case DataAccessStatus.blocked:
        return 'Blocked';
      case DataAccessStatus.monitored:
        return 'Monitored';
      case DataAccessStatus.quarantined:
        return 'Quarantined';
      case DataAccessStatus.pending:
        return 'Pending';
      default:
        return 'Unknown';
    }
  }

  private static _getResourceTypeIcon(
    itemId: string,
    resourceName: string,
    dataType: DataLayerItemType,
    fileExtension?: string | null
  ): string {
    return getDataLayerItemIcon(itemId, resourceName, dataType, fileExtension);
  }

  private static _getSourceTypeIcon(sourceType: DataSourceType): string {
    return getDataSourceTypeIcon(sourceType);
  }

  private static _getPlatformIcon(platform: Platform): string {
    return getPlatformLogoPath(platform);
  }

  private static _getStatusLabel(status: DataAccessStatus): StatusLabelEnum {
    switch (status) {
      case DataAccessStatus.active:
        return StatusLabelEnum.active;
      case DataAccessStatus.blocked:
        return StatusLabelEnum.failed; // Using failed for blocked
      case DataAccessStatus.monitored:
        return StatusLabelEnum.warning; // Using warning for monitored
      case DataAccessStatus.quarantined:
        return StatusLabelEnum.failed; // Using failed for quarantined
      case DataAccessStatus.pending:
        return StatusLabelEnum.inProgress; // Using inProgress for pending
      default:
        return StatusLabelEnum.open;
    }
  }

  private static _getResourceTooltip(dataLayerItem: IDataLayerItem): string {
    const name = dataLayerItem.resourceName;
    const itemType = dataLayerItem.dataType;
    const size = dataLayerItem.size ? ` (${formatFileSize(dataLayerItem.size)})` : '';
    return `${name}${size} - ${this._getDisplayDataType(itemType)}`;
  }

  private static _getAssociatedRisk(dataLayerItem: IDataLayerItem): string {
    // Get sensitivity from either new or legacy field
    const sensitivity = dataLayerItem.sensitivity_label?.toLowerCase();

    switch (sensitivity) {
      case 'critical':
        return 'Critical';
      case 'restricted':
        return 'High';
      case 'confidential':
        return 'Medium';
      case 'internal':
        return 'Low';
      case 'public':
        return 'Minimal';
      default:
        return 'Unknown';
    }
  }

  private static _getFindings(dataLayerItem: IDataLayerItem): string {
    // Return findings count if available
    if (dataLayerItem.findings && dataLayerItem.findings.length > 0) {
      return dataLayerItem.findings.length.toString();
    }
    return '';
  }

  private static _getFindingsBySeverity(dataLayerItem: IDataLayerItem): Record<DetectionRuleSeverity, number> {
    if (dataLayerItem.findingsBySeverity) {
      return dataLayerItem.findingsBySeverity;
    }
    return {
      [DetectionRuleSeverity.critical]: 0,
      [DetectionRuleSeverity.high]: 0,
      [DetectionRuleSeverity.medium]: 0,
      [DetectionRuleSeverity.low]: 0,
      [DetectionRuleSeverity.informational]: 0,
    };
  }
}
